import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import snsWebSdk, { type SnsWebSdk } from '@sumsub/websdk';
import { AppHeader } from '../components/AppHeader';
import { ApiService } from '../services/apiService';
import { buildSumsubWebSdkConf } from '../utils/sumsubWebSdkConf';
import './SumsubKyc.css';
import './SumsubWebsdkTest.css';
// @mcc-dev-preview:import
import { parseDevState } from '../dev/devPreview';

// @mcc-dev-preview:start scanner-test-dev-state
const SCANNER_TEST_DEV_STATES = ['form', 'loading', 'sdk-ready'] as const;
const DEV_PREVIEW_ACCESS_TOKEN = 'dev-preview-scanner-token';
// @mcc-dev-preview:end scanner-test-dev-state

const CONTAINER_ID = 'sumsub-websdk-test-container';
const MAX_LOG = 30;
const PREVIEW_POLL_MAX_ATTEMPTS = 20;
const PREVIEW_POLL_INTERVAL_MS = 1000;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type LogEntry = { t: number; kind: string; payload: unknown };

type ScannerApplicantPreviewData = NonNullable<
  Awaited<ReturnType<typeof ApiService.getPublicScannerTestApplicantPreview>>['data']
>;

type PreviewPollState =
  | { phase: 'idle' }
  | { phase: 'polling'; attempt: number }
  | { phase: 'done'; attempt: number }
  | { phase: 'timeout'; attempt: number }
  | { phase: 'error'; attempt: number; message: string };

function formatExtractedAddress(
  addr: {
    addressLine1?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
  } | null | undefined
): string {
  if (!addr) return '—';
  const parts = [addr.addressLine1, addr.city, addr.state, addr.postcode, addr.country].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
}

const SumsubWebsdkTest: React.FC = () => {
  const [searchParams] = useSearchParams();
  // @mcc-dev-preview:start scanner-test-dev-state
  const devState = parseDevState(searchParams, SCANNER_TEST_DEV_STATES);
  // @mcc-dev-preview:end scanner-test-dev-state
  const queryCardId = searchParams.get('cardId');
  const queryExternalUserId = searchParams.get('externalUserId');
  const queryEmail = searchParams.get('email');
  const themeParam = searchParams.get('theme');
  const issuingCountryParam = searchParams.get('issuingCountry');
  const customizationNameParam = searchParams.get('customizationName');
  const theme = themeParam === 'dark' || themeParam === 'light' ? themeParam : 'light';

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const [sessionExternalUserId, setSessionExternalUserId] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState(queryEmail ?? '');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sdkError, setSdkError] = useState<string | null>(null);
  const [events, setEvents] = useState<LogEntry[]>([]);
  const [kycStatus, setKycStatus] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [cardIdInput, setCardIdInput] = useState(queryCardId ?? '');
  const [externalUserIdInput, setExternalUserIdInput] = useState(queryExternalUserId ?? '');
  const [applicantPreview, setApplicantPreview] = useState<ScannerApplicantPreviewData | null>(null);
  const [previewPoll, setPreviewPoll] = useState<PreviewPollState>({ phase: 'idle' });
  const [manualPreviewLoading, setManualPreviewLoading] = useState(false);
  const [manualPreviewError, setManualPreviewError] = useState<string | null>(null);

  const activeCardId = cardIdInput.trim();
  const activeExternalUserId = externalUserIdInput.trim();

  const sessionExternalUserIdRef = useRef<string | null>(null);
  const pollRunnerActiveRef = useRef(false);
  const pollApplicantPreviewRef = useRef<() => Promise<void>>(async () => {});

  const pushLog = useCallback((kind: string, payload: unknown) => {
    setEvents((prev) => {
      const next = [...prev, { t: Date.now(), kind, payload }];
      return next.length > MAX_LOG ? next.slice(-MAX_LOG) : next;
    });
  }, []);

  const requestAccessToken = useCallback(
    async (emailForApplicant: string): Promise<{ token: string; userId: string }> => {
      const trimmed = emailForApplicant.trim();
      if (!trimmed) {
        throw new Error('Email is required');
      }
      const res = await ApiService.getPublicScannerTestSumsubAccessToken(
        activeCardId || undefined,
        activeExternalUserId || undefined,
        trimmed
      );
      if (!res.success || !res.data?.token || !res.data?.userId) {
        throw new Error(res.error || 'Could not get Sumsub access token');
      }
      return { token: res.data.token, userId: res.data.userId };
    },
    [activeCardId, activeExternalUserId]
  );

  const pollForApplicantPreview = useCallback(async (): Promise<void> => {
    const extId = sessionExternalUserIdRef.current;
    if (!extId) {
      pushLog('scannerTest.previewPoll.skip', { reason: 'no session externalUserId' });
      return;
    }
    if (pollRunnerActiveRef.current) {
      pushLog('scannerTest.previewPoll.skip', { reason: 'already running' });
      return;
    }
    pollRunnerActiveRef.current = true;
    setPreviewPoll({ phase: 'polling', attempt: 0 });
    let lastPayload: ScannerApplicantPreviewData | undefined;

    try {
      for (let attempt = 1; attempt <= PREVIEW_POLL_MAX_ATTEMPTS; attempt++) {
        setPreviewPoll({ phase: 'polling', attempt });
        const res = await ApiService.getPublicScannerTestApplicantPreview(extId);
        if (!res.success || !res.data) {
          setPreviewPoll({
            phase: 'error',
            attempt,
            message: res.error || 'Applicant preview request failed'
          });
          pushLog('scannerTest.previewPoll.error', { attempt, error: res.error });
          return;
        }
        lastPayload = res.data;
        if (res.data.hasDocumentResources) {
          setApplicantPreview(res.data);
          setPreviewPoll({ phase: 'done', attempt });
          pushLog('scannerTest.previewPoll.done', { attempt, imageCount: res.data.images.length });
          return;
        }
        await new Promise((r) => setTimeout(r, PREVIEW_POLL_INTERVAL_MS));
      }

      if (lastPayload) {
        setApplicantPreview(lastPayload);
      }
      setPreviewPoll({ phase: 'timeout', attempt: PREVIEW_POLL_MAX_ATTEMPTS });
      pushLog('scannerTest.previewPoll.timeout', {
        attempt: PREVIEW_POLL_MAX_ATTEMPTS,
        hadPayload: Boolean(lastPayload)
      });
    } finally {
      pollRunnerActiveRef.current = false;
    }
  }, [pushLog]);

  pollApplicantPreviewRef.current = pollForApplicantPreview;

  // @mcc-dev-preview:start scanner-test-dev-state
  useEffect(() => {
    if (!devState) return;

    setError(null);
    setSdkError(null);
    setApplicantPreview(null);
    setPreviewPoll({ phase: 'idle' });
    setManualPreviewError(null);
    setKycStatus(null);
    setStatusError(null);

    switch (devState) {
      case 'form':
        setLoading(false);
        setAccessToken(null);
        setSessionEmail(null);
        setSessionExternalUserId(null);
        sessionExternalUserIdRef.current = null;
        break;
      case 'loading':
        setLoading(true);
        setAccessToken(null);
        break;
      case 'sdk-ready':
        setLoading(false);
        setSessionEmail('preview@example.com');
        setSessionExternalUserId('scanner-test-dev-preview');
        sessionExternalUserIdRef.current = 'scanner-test-dev-preview';
        setAccessToken(DEV_PREVIEW_ACCESS_TOKEN);
        break;
      default:
        break;
    }
  }, [devState]);
  // @mcc-dev-preview:end scanner-test-dev-state

  const handleManualApplicantPreview = useCallback(async (): Promise<void> => {
    const extId = sessionExternalUserIdRef.current;
    setManualPreviewError(null);
    if (!extId) {
      setManualPreviewError('Start the WebSDK session first (need Sumsub userId from access token).');
      return;
    }
    setManualPreviewLoading(true);
    try {
      const res = await ApiService.getPublicScannerTestApplicantPreview(extId);
      if (!res.success || !res.data) {
        setManualPreviewError(res.error || 'Preview failed');
        return;
      }
      setApplicantPreview(res.data);
      setPreviewPoll({ phase: 'idle' });
      pushLog('scannerTest.previewManual', { hasDocumentResources: res.data.hasDocumentResources });
    } finally {
      setManualPreviewLoading(false);
    }
  }, [pushLog]);

  const handleStartWebSdk = useCallback(async (): Promise<void> => {
    const trimmed = emailInput.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setError('Enter a valid email address.');
      return;
    }
    setError(null);
    setSdkError(null);
    setApplicantPreview(null);
    setPreviewPoll({ phase: 'idle' });
    setManualPreviewError(null);
    setLoading(true);
    try {
      const { token, userId } = await requestAccessToken(trimmed);
      setSessionEmail(trimmed);
      setSessionExternalUserId(userId);
      sessionExternalUserIdRef.current = userId;
      setAccessToken(token);
      pushLog('scannerTest.sessionStarted', { email: trimmed, userId });
    } catch (e) {
      setAccessToken(null);
      setSessionEmail(null);
      setSessionExternalUserId(null);
      sessionExternalUserIdRef.current = null;
      setError(e instanceof Error ? e.message : 'Failed to load token');
    } finally {
      setLoading(false);
    }
  }, [emailInput, requestAccessToken, pushLog]);

  const fetchScannerStatus = useCallback(async (): Promise<void> => {
    if (!activeCardId) {
      throw new Error('Enter a cardId above (or pass ?cardId=...) to check KYC status.');
    }
    const res = await ApiService.getPublicScannerTestSumsubKycStatus(activeCardId);
    if (res.success && res.data) {
      setKycStatus(res.data.sumsubKycStatus);
      pushLog('scannerTest.status', res.data);
      return;
    }
    if (res.error) {
      pushLog('scannerTest.statusError', res.error);
      throw new Error(res.error);
    }
  }, [activeCardId, pushLog]);

  const handleCheckStatus = useCallback(async (): Promise<void> => {
    setStatusError(null);
    setStatusLoading(true);
    try {
      await fetchScannerStatus();
    } catch (e) {
      setStatusError(e instanceof Error ? e.message : 'Failed to check KYC status');
    } finally {
      setStatusLoading(false);
    }
  }, [fetchScannerStatus]);

  const expirationHandler = useCallback(async () => {
    if (!sessionEmail) {
      throw new Error('Session email missing; reload the page and start again.');
    }
    const { token, userId } = await requestAccessToken(sessionEmail);
    sessionExternalUserIdRef.current = userId;
    setSessionExternalUserId(userId);
    pushLog('token.refresh', { ok: true, userId });
    return token;
  }, [requestAccessToken, sessionEmail, pushLog]);

  const sumsubTestEnv = process.env.REACT_APP_SUMSUB_TEST_ENV === 'true';

  useEffect(() => {
    // @mcc-dev-preview:start scanner-test-dev-state
    if (devState) return undefined;
    // @mcc-dev-preview:end scanner-test-dev-state
    if (!accessToken) return undefined;

    let instance: SnsWebSdk | null = null;

    const b = snsWebSdk.init(accessToken, expirationHandler);
    if (sumsubTestEnv) {
      b.onTestEnv();
    }

    instance = b
      .withConf(
        buildSumsubWebSdkConf({
          lang: 'en',
          theme,
          issuingCountryOverride: issuingCountryParam,
          customizationNameOverride: customizationNameParam,
        })
      )
      .withOptions({ addViewportTag: false, adaptIframeHeight: true })
      .on('idCheck.onStepCompleted', (payload) => {
        console.log('Sumsub WebSDK onStepCompleted', payload);
        pushLog('idCheck.onStepCompleted', payload);
        void pollApplicantPreviewRef.current();
      })
      .on('idCheck.onError', (err) => {
        console.log('Sumsub WebSDK onError', err);
        pushLog('idCheck.onError', err);
        setSdkError(err.reason || err.error || 'WebSDK error');
      })
      .build();

    instance.launch(`#${CONTAINER_ID}`);

    return () => {
      instance?.destroy();
      instance = null;
    };
  }, [accessToken, expirationHandler, sumsubTestEnv, theme, issuingCountryParam, customizationNameParam, pushLog, devState]);

  return (
    <div className="sumsub-kyc-page ev-page ev-page-enter sumsub-websdk-test-page">
      <AppHeader />
      <main className="sumsub-kyc-main sumsub-websdk-test-main">
        <div className="sumsub-kyc-card ev-shell-card ev-animate-in">
          <h1 className="ev-display-headline" style={{ color: 'var(--ev-text-on-card)', fontSize: '22px' }}>
            Sumsub scanner test (dev)
          </h1>
          <p className="ev-subtext">
            Enter an email address and submit to pre-create the Sumsub applicant and launch the WebSDK. Optional{' '}
            <code>cardId</code> / <code>externalUserId</code> below define the Sumsub <code>userId</code> (defaults to
            a generated <code>scanner-test-…</code> id when both are empty). After each WebSDK step, the page polls
            Sumsub for document resources (1s interval, up to {PREVIEW_POLL_MAX_ATTEMPTS} tries) and loads Azure DI
            address extraction when <code>DI_*</code> is configured on the API. Set <code>REACT_APP_SUMSUB_TEST_ENV=true</code>{' '}
            for sandbox; add <code>?theme=dark</code>, <code>?issuingCountry=AUS</code> (alpha-3),{' '}
            <code>?customizationName=…</code>, or <code>?email=…</code> to pre-fill this form.
          </p>

          <div className="sumsub-websdk-test-email-block">
            <label className="ev-subtext" htmlFor="sumsub-test-email-input">
              Email address
            </label>
            <div className="sumsub-websdk-test-email-row">
              <input
                id="sumsub-test-email-input"
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                disabled={loading}
              />
              <button type="button" className="ev-btn ev-btn-primary" onClick={() => void handleStartWebSdk()} disabled={loading}>
                {loading ? 'Starting…' : 'Submit and start WebSDK'}
              </button>
            </div>
          </div>

          <label className="ev-subtext" htmlFor="sumsub-test-external-user-id-input">
            External user ID (optional)
          </label>
          <input
            id="sumsub-test-external-user-id-input"
            type="text"
            value={externalUserIdInput}
            onChange={(e) => setExternalUserIdInput(e.target.value)}
            placeholder="Sumsub externalUserId; overrides cardId-based id when set"
            autoComplete="off"
          />
          <label className="ev-subtext" htmlFor="sumsub-test-card-id-input">
            Card ID (optional)
          </label>
          <input
            id="sumsub-test-card-id-input"
            type="text"
            value={cardIdInput}
            onChange={(e) => setCardIdInput(e.target.value)}
            placeholder="Enter cardId for status checks"
          />
          <button type="button" onClick={() => void handleCheckStatus()} disabled={statusLoading}>
            {statusLoading ? 'Checking status…' : 'Check KYC status'}
          </button>
          {kycStatus && <p className="ev-subtext">Current KYC status: {kycStatus}</p>}
          {statusError && <p className="ev-subtext">{statusError}</p>}

          {sessionExternalUserId && (
            <p className="ev-subtext sumsub-websdk-test-session-id">
              Session Sumsub userId: <code>{sessionExternalUserId}</code>
            </p>
          )}

          {previewPoll.phase === 'polling' && (
            <p className="ev-subtext sumsub-websdk-test-poll-status" role="status">
              Fetching applicant and document resources… attempt {previewPoll.attempt}/{PREVIEW_POLL_MAX_ATTEMPTS}
            </p>
          )}
          {previewPoll.phase === 'done' && (
            <p className="ev-subtext sumsub-websdk-test-poll-status sumsub-websdk-test-poll-done" role="status">
              Document resources loaded (poll attempt {previewPoll.attempt}).
            </p>
          )}
          {previewPoll.phase === 'timeout' && (
            <p className="ev-subtext sumsub-websdk-test-poll-status sumsub-websdk-test-poll-warn" role="status">
              Polling finished without document resources from Sumsub. Use “Refresh applicant preview” or complete more
              WebSDK steps. Last response is shown below if available.
            </p>
          )}
          {previewPoll.phase === 'error' && (
            <div className="sumsub-kyc-error" role="alert">
              <p>Applicant preview poll failed: {previewPoll.message}</p>
            </div>
          )}

          <div className="sumsub-websdk-test-preview-actions">
            <button
              type="button"
              className="ev-btn ev-btn-secondary"
              onClick={() => void handleManualApplicantPreview()}
              disabled={manualPreviewLoading || !sessionExternalUserId}
            >
              {manualPreviewLoading ? 'Loading preview…' : 'Refresh applicant preview'}
            </button>
            {manualPreviewError && (
              <p className="ev-subtext sumsub-websdk-test-manual-error" role="alert">
                {manualPreviewError}
              </p>
            )}
          </div>

          {loading && <p className="ev-subtext">Pre-creating applicant and fetching access token…</p>}
          {error && (
            <div className="sumsub-kyc-error" role="alert">
              <p>{error}</p>
            </div>
          )}
          {sdkError && (
            <div className="sumsub-kyc-error" role="alert">
              <p>{sdkError}</p>
            </div>
          )}

          {!accessToken && !loading && (
            <p className="ev-subtext sumsub-websdk-test-placeholder">Submit your email above to load the WebSDK widget.</p>
          )}

          {applicantPreview && (
            <section className="sumsub-websdk-test-preview" aria-label="Applicant and document preview">
              <h2 className="sumsub-websdk-test-log-title">Applicant preview</h2>
              <dl className="sumsub-websdk-test-preview-dl">
                <dt>First name</dt>
                <dd>{applicantPreview.names?.firstName ?? '—'}</dd>
                <dt>Last name</dt>
                <dd>{applicantPreview.names?.lastName ?? '—'}</dd>
                <dt>Date of birth</dt>
                <dd>{applicantPreview.dateOfBirth ?? '—'}</dd>
                <dt>Applicant id</dt>
                <dd>
                  <code>{applicantPreview.applicantId}</code>
                </dd>
                <dt>Inspection id</dt>
                <dd>{applicantPreview.inspectionId ?? '—'}</dd>
                <dt>Azure DI</dt>
                <dd>
                  {applicantPreview.documentIntelligenceConfigured
                    ? 'Configured (DI ran on eligible images)'
                    : 'Not configured on API (set DI_ENDPOINT / DI_KEY)'}
                </dd>
              </dl>

              <h3 className="sumsub-websdk-test-preview-sub">Document images</h3>
              {applicantPreview.images.length === 0 ? (
                <p className="ev-subtext">No inspection images in this response yet.</p>
              ) : (
                <ul className="sumsub-websdk-test-preview-images">
                  {applicantPreview.images.map((img) => (
                    <li key={img.imageId} className="sumsub-websdk-test-preview-image-card">
                      <div className="sumsub-websdk-test-preview-image-card-body">
                        <div className="sumsub-websdk-test-preview-image-meta">
                          <div>
                            <code>{img.imageId}</code>
                          </div>
                          {img.idDocDef ? (
                            <div className="ev-subtext">
                              {(img.idDocDef as { idDocType?: string }).idDocType ?? ''}{' '}
                              {(img.idDocDef as { idDocSubType?: string | null })?.idDocSubType ?? ''}
                            </div>
                          ) : null}
                          {img.fetchError ? (
                            <div className="sumsub-websdk-test-manual-error">{img.fetchError}</div>
                          ) : null}
                          {img.diSkippedReason ? (
                            <div className="ev-subtext">DI skipped: {img.diSkippedReason}</div>
                          ) : null}
                          <div className="ev-subtext">DI address: {formatExtractedAddress(img.extractedAddress)}</div>
                        </div>
                        {img.dataBase64 && img.contentType ? (
                          <img
                            className="sumsub-websdk-test-preview-img"
                            src={`data:${img.contentType};base64,${img.dataBase64}`}
                            alt=""
                          />
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <h3 className="sumsub-websdk-test-preview-sub">Sumsub applicant JSON</h3>
              <pre className="sumsub-websdk-test-preview-json">{JSON.stringify(applicantPreview.applicant, null, 2)}</pre>
            </section>
          )}

          {accessToken && !error && (
            <>
              {/* @mcc-dev-preview:start scanner-test-dev-state */}
              {devState === 'sdk-ready' && (
                <div
                  className="sumsub-websdk-test-dev-preview-placeholder"
                  style={{
                    minHeight: 280,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed var(--ev-card-border)',
                    borderRadius: 'var(--ev-card-radius)',
                    color: 'var(--ev-text-muted)',
                    marginBottom: '1rem',
                  }}
                >
                  Sumsub scanner WebSDK preview (dev)
                </div>
              )}
              {/* @mcc-dev-preview:end scanner-test-dev-state */}
              <div className="sumsub-kyc-sdk-wrap sumsub-websdk-test-sdk">
                <div id={CONTAINER_ID} />
              </div>
              <section className="sumsub-websdk-test-log" aria-label="WebSDK events">
                <h2 className="sumsub-websdk-test-log-title">Recent events</h2>
                {events.length === 0 ? (
                  <p className="ev-subtext">
                    Complete a step in the widget to see <code>idCheck.onStepCompleted</code> here.
                  </p>
                ) : (
                  <ol className="sumsub-websdk-test-log-list">
                    {events.map((e) => (
                      <li key={e.t}>
                        <span className="sumsub-websdk-test-log-kind">{e.kind}</span>
                        <pre className="sumsub-websdk-test-log-payload">{JSON.stringify(e.payload, null, 2)}</pre>
                      </li>
                    ))}
                  </ol>
                )}
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default SumsubWebsdkTest;
